import { CollapsibleArea, LoaderSpin } from '@framework/ui';
import { Alert, Empty, Typography } from 'antd';
import clx from 'classnames';
import * as React from 'react';
import { useState } from 'react';
import { DocumentalContent } from './component/DocumentalContent';
import { DocumentalDocumentRow } from './component/DocumentalDocumentRow';
import { DocumentalFolderButton } from './component/DocumentalFolderButton';
import { DocumentalRow } from './component/DocumentalRow';
import { useDocumentDossierQuery } from './useDocumentalDossierQuery';

export interface IDocumentalDossierListProps {
  dossierId?: string | null;
  documentalToken?: string | null;
  isRoot?: boolean;
}

const DOCUMENTAL_AUTH_TOKEN_HEADER = 'tokenFascicoli';

interface IAuthDossierState {
  dossierId: string;
  token: string;
}

/**
 * Mostra un fascicolo e permette di navigare nei sottofascicoli e
 * scaricare i documenti.
 */
export function DocumentalDossierList(props: IDocumentalDossierListProps) {
  const { dossierId, documentalToken, isRoot } = props;

  const [isOpen, setIsOpen] = useState(isRoot ?? false);

  const dossierResult = useDocumentDossierQuery(dossierId, {
    documentalToken: documentalToken ?? '',
    enabled: isOpen || isRoot,
  });

  const dossier = dossierResult.dossier;
  const documents = dossierResult.documents;
  const documentDownloadToken = dossierResult.documentDownloadToken;

  if (dossierResult.error) {
    return (
      <Alert
        showIcon
        message="Impossibile caricare il fascicolo"
        description={dossierResult.error?.message}
        type="error"
      />
    );
  }

  if (dossierResult.isLoading) {
    return (
      <div style={{ height: 32, position: 'relative' }}>
        <LoaderSpin full />
      </div>
    );
  }

  if (!dossier) {
    return (
      <Empty
        description={
          <Typography.Text>
            Non ci sono fascicoli per questo procedimento
          </Typography.Text>
        }
      />
    );
  }

  return (
    <div>
      <DocumentalRow
        key={dossier.idFascicolo}
        className={clx({ 'is-root-dossier': isRoot })}
      >
        <DocumentalFolderButton
          id={`collapse-area-button-${dossier.idFascicolo}`}
          type="link"
          onClick={() => {
            // !isRoot &&
            setIsOpen(open => !open);
          }}
          isOpen={isOpen}
        >
          {dossier.nomeFascicolo}
          {dossier.numero ? ` - ${dossier.numero} ` : ''} - {dossier.oggetto}
        </DocumentalFolderButton>
      </DocumentalRow>

      <DocumentalContent>
        <CollapsibleArea
          id={`collapse-area-${dossier.idFascicolo}`}
          buttonId={`collapse-area-button-${dossier.idFascicolo}`}
          opened={isOpen}
        >
          {dossier.sottoFascicoli.length > 0 && (
            <div>
              {dossier.sottoFascicoli?.map(subdossier => {
                return (
                  <DocumentalDossierList
                    key={subdossier.idFascicolo}
                    dossierId={subdossier.idFascicolo}
                    documentalToken={documentalToken}
                  />
                );
              })}
            </div>
          )}

          {documents?.map(document => (
            <DocumentalDocumentRow
              dossierId={dossier.idFascicolo}
              key={document.idFileDocumento}
              document={document}
              documentToken={documentDownloadToken}
            />
          ))}

          {/* Se non ci sono sottofascicoli o documenti mostriamo un messaggio */}
          {(!documents || documents.length < 1) &&
            dossier.sottoFascicoli.length < 1 && (
              <DocumentalRow className={clx('empty-dossier')}>
                Non ci sono documenti in questo fascicolo
              </DocumentalRow>
            )}
        </CollapsibleArea>
      </DocumentalContent>
    </div>
  );
}
